import React, { useMemo, useState, useEffect } from 'react';
import { useIntl, useSelector } from 'umi';
import Filter from './components/Filter';
import StoreRankDetailsCore from '@/pages/intelligence/pages/TopCharts/components/StoreRankDetailsCore';
import ExportTable from '@/pages/intelligence/components/ExportTable';
import { DataSourceBubbleInfo } from '@/pages/intelligence/components/DataSourceComponents';
import useFilterExtend from '@intelligence/hooks/useFilterExtend';
import useStoreRankDetailsCoreUI from '@/pages/intelligence/pages/TopCharts/components/StoreRankDetailsCore/hooks/useStoreRankDetailsCoreUI';
import { getStoreRankDetailsCoreUIData } from '@/pages/intelligence/pages/TopCharts/components/StoreRankDetailsCore/utils/dataTransfer';
import useStoreTopRegionOptionsMap from '@intelligence/v3/_hooks/useStoreTopRegionOptionsMap';
import useInitialTopChartsName from '@/pages/intelligence/pages/StoreRanks/components/StoreRankMoreCore/hooks/useInitialTopChartsName';
import { fetchStoreRanksDetailsData } from '@/pages/intelligence/services/storeRank';
import { fetchConsoleStoreRankDetailsCountriesRanksPlus } from './services';
import { updateURLSearchParams } from '@intelligence/utils/commonUtils';
import { getConsoleDataSourceInfo } from '@/pages/intelligence/utils/getDataSourceInfo';
import {
  analysisPlatformTopInfo,
  getTopSource,
  checkNintendoUsOnly,
} from '@/pages/intelligence/_config/storeRank/utils';
import {
  CONSOLE_SOURCES,
  CONSOLE_RANK_METRIC_MONTHLY_NAMES_MAPPING,
  CONSOLE_STORE_RANK_TOPS_NAMES_MAPPING,
  CONSOLE_PLATFORM_NINTENDO,
} from '@/pages/intelligence/_config/storeRank/config';
import {
  getIsSectionalRank,
  getParams,
  getIsMonthlyRank,
  getCountriesValues,
  handleResData,
  getInitialFilter,
} from './utils';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { getCountryCodeIntersection } from '@intelligence/utils/filterUtils';
import useEarliestTime from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/utilsTmp/useEarliestTime';
import { DBI_TOPCHARTS } from '@/pages/intelligence/models/topcharts';
import '@/pages/intelligence/assets/scss/StoreRanks/details.scss';
import type { IStoreRankDetailsProps } from '@/pages/intelligence/pages/TopCharts/components/StoreRankDetails';
import type {
  IStoreRankDetailsResData,
  IStoreRankDetailsTablesSourceData,
  IStoreRankDetailsCountriesRanks,
} from '@/pages/intelligence/types/storeRank';
import type { IStoreRankDetailsFilter } from './types';
import TipContext from '@home/components/TipContextComponents/TipContext';
import { CONSOLE_ENTITY_TYPE, CONSOLE_PLATFORMS, PLAYSTATION_PLATFORM } from '@intelligence/v3/constants/topcharts';
import { getFirstWeekSinceReleaseDates, isFirstWeekSinceReleaseShortcut } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/utils';
import { formatDateStr } from '@intelligence/v3/utils/date';

const DEFAULT_FILTER = {
  granularity: 'daily',
  start_date: '',
  end_date: '',
  platform: PLAYSTATION_PLATFORM,
  top: 'best selling',
  regions: ['global', 'us/canada', 'europe'],
  markets: ['us'],
};

type IConsoleStoreRankDetailsProps = IStoreRankDetailsProps;

const ConsoleDetailsStoreRank: React.FC<IConsoleStoreRankDetailsProps> = (props) => {
  const { entityType = CONSOLE_ENTITY_TYPE, gameId, countryList, basicInfo } = props;

  const { formatMessage } = useIntl();

  const { isOperateDetail, detailFilterExtend } = useFilterExtend({});
  const initialSource = useInitialTopChartsName(CONSOLE_SOURCES);
  const { platform: initialPlatform, top: initialTop } = analysisPlatformTopInfo(initialSource);
  const defaultFilter = {
    ...DEFAULT_FILTER,
    platform: initialPlatform || DEFAULT_FILTER.platform,
    top: initialTop || DEFAULT_FILTER.top,
  };
  const initialFilter = getInitialFilter(isOperateDetail, detailFilterExtend, defaultFilter);
  const [filter, setFilter] = useState<IStoreRankDetailsFilter>(initialFilter);
  const topchartsState = useSelector((state: any) => state?.[DBI_TOPCHARTS] || {});
  const [isLoading, setIsLoading] = useState(true);
  const [storeRankDetailsCoreResData, setStoreRankDetailsCoreResData] = useState<
    IStoreRankDetailsResData[]
  >([]);

  const [currentFetchSource, setCurrentFetchSource] = useState<string[]>([]);

  const {
    isLoading: isRegionsOptionsLoading,
    regionsOptions,
    regionsOptionsMap,
  } = useStoreTopRegionOptionsMap(filter?.platform, {
    entityType,
    platforms: CONSOLE_PLATFORMS,
  });

  const nintendoUsOnly = useMemo(
    () => checkNintendoUsOnly(`${filter?.platform} ${filter?.top}`),
    [JSON.stringify(filter)],
  );

  const { earliestPeriod, updateEarliestDateData, baseReleaseTimeValue } = useEarliestTime({
    entityType,
    basicInfo,
    topchartsState,
  });

  const downloadParams = useMemo(() => {
    const params = getParams(filter);
    return {
      entity_type: entityType,
      app_ids: gameId ? [gameId] : [],
      edition_method: 'aggregated',
      region_type: 'market',
      regions: params?.markets,
      date_type: params?.granularity,
      start_date: params?.startDate,
      end_date: params?.endDate,
      platform: filter?.platform || '',
    };
  }, [entityType, gameId, filter]);

  const isMonthlyRank = getIsMonthlyRank({
    platform: filter?.platform,
    top: filter?.top,
    granularity: filter?.granularity,
  });

  // 是否为分段排名模式(日粒度rank 并且国家数量大于1个时，按排名分段展示)
  const isSectionalRank = getIsSectionalRank({
    isMonthlyRank,
    markets: filter?.markets,
  });

  const formatAreaName = (areaCode: string): string => {
    const dimensionKey = CONSOLE_RANK_METRIC_MONTHLY_NAMES_MAPPING[areaCode];
    let localeDimensionName;
    try {
      localeDimensionName = formatMessage({ id: dimensionKey });
    } catch (exception) {
      localeDimensionName = areaCode;
    }
    return localeDimensionName;
  };
  const isNitendo = filter?.platform === CONSOLE_PLATFORM_NINTENDO;
  const platformInfo = getConsoleDataSourceInfo(filter?.platform);
  const platformName = platformInfo?.name ?? '';
  const platformIcon = platformInfo?.icon ?? '';
  const topNameLocaleKey = CONSOLE_STORE_RANK_TOPS_NAMES_MAPPING?.[filter?.top];
  const topNameLocale = formatMessage({ id: topNameLocaleKey });

  const { visualisOption, tablesOption } = useStoreRankDetailsCoreUI({
    isLoading,
    isSectionalRank,
    visualisHelpperOptionConfig: {
      isShowYAxisName: true,
      yAxisName: isSectionalRank
        ? formatMessage({ id: 'dbi_count' })
        : formatMessage({ id: 'dbi_rank' }),
      yAxisNamePadding: isSectionalRank ? [0, -5, 0, 0] : null,
      // tooltipHeaderLeftTitle: toFirstUpperCase(filter?.top),
      tooltipHeaderLeftTitle: topNameLocale,
      tooltipHeaderLeftIcon: platformInfo?.icon,
      rankDimensionsNameFormatter: (dimension: string) => {
        // 月粒度rank
        if (isMonthlyRank) return formatAreaName(dimension);
        // 非月粒度rank
        // return [platformName, toFirstUpperCase(filter?.top)].join('_');
        // return [platformName, topNameLocale].join('_');
        return topNameLocale;
      },
    },
    tablesHelpperOptionConfig: {
      rankDimensionsNameFormatter: (dimension: string) => {
        // 月粒度rank
        if (isMonthlyRank) return formatAreaName(dimension);
        // 非月粒度rank
        // return toFirstUpperCase(filter?.top);

        return topNameLocale;
      },
    },
    data: storeRankDetailsCoreResData,
  });
  const firstWeekVisualisOption = useMemo(() => {
    if (!isFirstWeekSinceReleaseShortcut(topchartsState?.selectedTimeShortcutType)) {
      return visualisOption;
    }
    const firstWeekSinceReleaseDates = getFirstWeekSinceReleaseDates({
      dateType: filter?.granularity,
      startDate: filter?.start_date,
      endDate: filter?.end_date,
      releaseDate: baseReleaseTimeValue,
    });
    if (!firstWeekSinceReleaseDates.length) {
      return visualisOption;
    }
    const responseDataMap = new Map<string, IStoreRankDetailsResData>(
      storeRankDetailsCoreResData.map((item): [string, IStoreRankDetailsResData] => {
        return [formatDateStr(item?.date), item];
      }),
    );
    const displayData = firstWeekSinceReleaseDates.map((date) => {
      const dateItem = responseDataMap.get(date);
      if (dateItem) {
        return { ...dateItem, date };
      }
      return { date, metric: [] };
    });
    const { sourceDataDimensions, visualisSourceData } = getStoreRankDetailsCoreUIData(displayData);
    return {
      ...visualisOption,
      sourceDataDimensions,
      sourceData: visualisSourceData,
    };
  }, [
    topchartsState?.selectedTimeShotcutType,
    visualisOption,
    storeRankDetailsCoreResData,
    filter?.granularity,
    filter?.start_date,
    filter?.end_date,
    baseReleaseTimeValue,
  ]);

  const updateStoreRankDetailsCoreData = async (filterPayload?: IStoreRankDetailsFilter) => {
    setIsLoading(true);
    const { granularity, startDate, endDate, sources, regions, markets } = getParams(
      filter,
      filterPayload,
    );

    setCurrentFetchSource(sources);

    try {
      let filterTmp = {
        ...filter,
        ...filterPayload,
      };
      const isMonthlyRank = getIsMonthlyRank({
        platform: filterTmp?.platform,
        top: filterTmp?.top,
        granularity: filterTmp?.granularity,
      });
      // 是否为分段排名模式(按排名分段展示)
      const isSectionalRank = getIsSectionalRank({
        isMonthlyRank,
        markets,
      });

      const paramValue = {
        entity_type: entityType,
        source: sources,
        // 这个字段仅在mobile有效，pc、console传空，防止接口有必传校验导致接口报错（三端同一个接口）
        unified_id: '',
        app_ids: [gameId],
        region_type: 'market',
        regions: isMonthlyRank
          ? regions
          : /* checkNintendoUsOnly(sources?.[0]) ? ['us'] :*/ markets,
        granularity,
        start_date: startDate,
        end_date: endDate,
      };

      const res = await fetchStoreRanksDetailsData(paramValue);
      const dateTypeReq = res?.req?.granularity ?? 'daily';
      const startDateReq = res?.req?.start_date ?? '';
      const endDateReq = res?.req?.end_date ?? '';
      filterTmp = {
        ...filterTmp,
        granularity: dateTypeReq,
        start_date: startDateReq,
        end_date: endDateReq,
      };
      const storeRankDetailsCoreResDataTmp = handleResData(res?.data, {
        isMonthlyRank,
        isSectionalRank,
      });
      setFilter(filterTmp);
      setStoreRankDetailsCoreResData(storeRankDetailsCoreResDataTmp);

      // 更新最早有数据时间
      updateEarliestDateData({
        entity_type: paramValue.entity_type,
        data_sources: paramValue.source,
        app_ids: paramValue.app_ids,
        metric: ['rank'],
        unified_id: paramValue?.unified_id,
        region_type: paramValue.region_type,
        regions: paramValue.regions,
        dateType: paramValue.granularity,
      });
    } catch (exception) {
      setStoreRankDetailsCoreResData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // 分段排名时，国家排名子表格 获取数据的方法
  const fetchCountriesRankData = async (
    params: IStoreRankDetailsTablesSourceData,
  ): Promise<IStoreRankDetailsCountriesRanks[]> => {
    const { sources } = getParams(filter);
    const res = await fetchConsoleStoreRankDetailsCountriesRanksPlus({
      edition_id: gameId,
      source: sources,
      markets: filter?.markets,
      end_date: params?.date ?? '',
    });
    return res;
  };

  const formatRegionParams = (markets: string[], countryCode: string[]) => {
    // 特殊处理国家值为交集之后的值
    const result = getCountryCodeIntersection({
      markets,
      countriesMarkets: countryCode,
      disableNoChina: true,
      defaultMarkets: [],
    });
    return result;
  };

  useEffect(() => {
    if (!gameId) return;
    if (isRegionsOptionsLoading) return;
    const regionsOptions = regionsOptionsMap?.[filter?.platform];
    const regionsOptinosValues = getCountriesValues(regionsOptions);
    let filterTmp = {
      ...filter,
    };
    if (!isOperateDetail) {
      filterTmp = {
        ...filterTmp,
        markets: isNitendo ? ['us'] : regionsOptinosValues ?? [],
      };
    } else {
      const result = formatRegionParams(filterTmp?.markets, regionsOptinosValues);
      filterTmp = {
        ...filterTmp,
        markets: result ?? [],
      };
    }
    setFilter(filterTmp);
    updateStoreRankDetailsCoreData(filterTmp);
  }, [entityType, gameId, isRegionsOptionsLoading]);

  return (
    <div className="dbi-store-rank-details-page">
      <Filter
        className="dbi-store-rank-details-page_filter"
        regionsOptions={regionsOptions}
        regionsOptionsMap={regionsOptionsMap}
        filter={filter}
        onConfirm={(payload: any) => {
          const regionsOptions = regionsOptionsMap?.[payload?.platform];
          const regionsOptinosValues = getCountriesValues(regionsOptions);
          // 对国家进行取交集处理
          const result = formatRegionParams(payload?.markets, regionsOptinosValues);
          const filterTmp = {
            ...filter,
            ...payload,
            markets: result,
          };
          updateStoreRankDetailsCoreData(filterTmp);
          setFilter(filterTmp);
          const source = getTopSource(filterTmp?.platform, filterTmp?.top);
          updateURLSearchParams({ source });
        }}
        onReset={() => {}}
        nintendoUsOnly={nintendoUsOnly}
        isNitendo={isNitendo}
        sinceReleaseTime={baseReleaseTimeValue}
        earliestDataTime={earliestPeriod}
      />
      <StoreRankDetailsCore
        isLoading={isLoading}
        entityType={entityType}
        gameId={gameId}
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {formatMessage({
              id: isSectionalRank
                ? 'dbi_store_rank_visualis_card_title_multipe'
                : 'dbi_store_rank_visualis_card_title_single',
            })}
            <TipContext
              className="tip-text-hd dbi_pc_store_rank_tips"
              tipText={formatMessage({ id: 'dbi_info_rank_trend_tip_desc' })}
            />
          </div>
        }
        visualisOption={firstWeekVisualisOption}
        tablesOption={{
          ...tablesOption,
          platformIcon: () => platformIcon,
          platformIconTips: (dimension: string) => {
            const dimensionName = isMonthlyRank ? formatAreaName(dimension) : topNameLocale;
            return (
              <DataSourceBubbleInfo
                metricKey={filter?.top}
                entityType={entityType}
                dataOfDom={
                  <>
                    <img src={platformIcon} alt="" />
                    {dimensionName ?? ''}
                  </>
                }
                comesFormDom={
                  <>
                    <img src={platformIcon} alt="" />
                    {platformName}
                  </>
                }
                dataSource={platformName}
              />
            );
          },
        }}
        pageLogConfig={dbiPageLogMes.consoleGameDetailsStoreRank}
        countries={countryList}
        fetchCountriesRankData={fetchCountriesRankData}
        currentFetchSource={currentFetchSource}
        downloadBtn={
          <ExportTable
            classModalName=""
            entityType={entityType}
            downloadModalSize={820}
            // downloadModalSize={504}
            isDisableDataAmount={true}
            isShowMaxDownloadRowsTips={false}
            // @ts-ignore
            businessType="console-details-store-rank"
            isDisableDownload={isLoading}
            downloadLogConfigKey="consoleGameDetailsStoreRankDownloadExcel"
            total={tablesOption?.sourceData?.length ?? 0}
            tableParams={downloadParams}
            newStyleFileName={`${basicInfo?.name}_Store Rank`}
          />
        }
      />
    </div>
  );
};

export default ConsoleDetailsStoreRank;
